import type { LiveStatusDoc } from '../types';
import { StatusBadge } from './StatusBadge';
import { useTimeElapsed } from '../hooks/useTimeElapsed';
import { Clock, Video } from 'lucide-react';
import { formatJudgeName } from '../utils/formatJudgeName';
import { formatHearingType } from '../utils/formatHearingType';
import { openExternalUrl } from '../utils/openExternalUrl';



function getHearingTypeClass(raw: string | null | undefined): string {
  if (!raw) return '';
  const type = formatHearingType(raw).toUpperCase();
  if (type.includes('ADMISSION') || type.includes('PRONOUNCING') || type.includes('DEFAULT')) return 'court-card__hearing-tag--green';
  if (type.includes('FINAL HEARING') || type.includes('SPECIALLY')) return 'court-card__hearing-tag--red';
  if (type.includes('ADJOURNED') || type.includes('MENTIONED')) return 'court-card__hearing-tag--yellow';
  return '';
}


function stripItemPrefix(val: string | number | null | undefined): string {
  if (val == null) return '';
  return String(val).replace(/^item\s*(no\.?\s*)?/i, '').trim();
}

interface Props {
  court: LiveStatusDoc;
  onClick: () => void;
}

export function CourtCard({ court, onClick }: Props) {
  const { court: courtId, judgeName, status, currentCaseDetail, currentItemNumber, currentCaseNumber, location, lastCaseAt } = court;
  const detail = currentCaseDetail;
  const elapsed = useTimeElapsed(status === 'live' ? lastCaseAt : null);
  const formattedJudges = judgeName.length > 0
    ? judgeName.map(formatJudgeName).join(' & ')
    : null;

  return (
    <article
      id={`court-card-${location}-${courtId}`}
      className={`court-card court-card--${status} ${status === 'live' ? 'court-card--clickable' : ''}`}
      onClick={status === 'live' ? onClick : undefined}
      role={status === 'live' ? 'button' : undefined}
      tabIndex={status === 'live' ? 0 : undefined}
    >
      {/* ── HEADER ── */}
      <div className="court-card__header">
        <div className="court-card__meta">
          <div className="court-card__title-row">
            <span className="court-card__number">Court {courtId.toUpperCase().replace('_', ' ')}</span>
            <div className="court-card__badges">
              {judgeName.length > 1 && (
                <span className="court-card__db-badge" title="Division Bench">DB</span>
              )}
              {court.vcLink && (
                <a
                  href={court.vcLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="court-card__vc-badge"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openExternalUrl(court.vcLink!);
                  }}
                  title="Join via Video Conferencing"
                >
                  <Video size={11} strokeWidth={2.5} />
                  <span>VC</span>
                </a>
              )}
              <StatusBadge status={status} />
            </div>
          </div>
          {(formattedJudges || court.judgeHeading) && (
            <span className="court-card__judge">
              {formattedJudges ?? court.judgeHeading}
            </span>
          )}
        </div>
      </div>

      {/* ── LIVE BODY ── */}
      {status === 'live' && (
        <div className="court-card__body">
          {/* Item + elapsed + hearing type */}
          <div className="court-card__hero-row">
            <div className="court-card__item-block">
              <span className="court-card__item-label">ITEM</span>
              <span className="court-card__item-number-large">
                {stripItemPrefix(currentItemNumber)}
              </span>
            </div>
            <div className="court-card__tags-col">
              {elapsed && (
                <span className="court-card__elapsed">
                  <Clock size={13} />
                  {elapsed}
                </span>
              )}
              {detail?.hearingType && formatHearingType(detail.hearingType) && (
                <span className={`court-card__hearing-tag ${getHearingTypeClass(detail.hearingType)}`}>
                  {formatHearingType(detail.hearingType)}
                </span>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {(court.totalCasesToday ?? 0) > 0 && (court.itemsCalledToday?.length ?? 0) > 0 && (
            <div className="court-card__progress-container">
              <div className="court-card__progress-track">
                <div
                  className="court-card__progress-bar"
                  style={{ width: `${Math.min(100, ((court.itemsCalledToday?.length ?? 0) / (court.totalCasesToday ?? 1)) * 100)}%` }}
                />
              </div>
              <div className="court-card__progress-text">
                {court.itemsCalledToday?.length ?? 0} of {court.totalCasesToday} cases called
              </div>
            </div>
          )}

          {/* Case number */}
          <div className="court-card__case-number">
            {detail?.caseNumber ?? currentCaseNumber}
          </div>

          {/* Parties */}
          {detail && (
            <div className="court-card__parties">
              <span className="court-card__petitioner">{detail.petitioner}</span>
              <span className="court-card__vs"> v. </span>
              <span className="court-card__respondent">{detail.respondents[0]}</span>
            </div>
          )}

          {/* Previous case */}
          {court.previousItemNumber && court.previousCaseNumber && (
            <div className="court-card__previous-case">
              <span className="court-card__previous-label">Previously:</span>
              {' '}Item {stripItemPrefix(court.previousItemNumber)} &bull; {court.previousCaseNumber}
            </div>
          )}
        </div>
      )}

      {status === 'idle' && (
        <div className="court-card__body court-card__body--muted">No case currently being heard</div>
      )}
      {status === 'list_over' && (
        <div className="court-card__body court-card__body--muted">List over for the day</div>
      )}
      {status === 'no_data' && (
        <div className="court-card__body court-card__body--muted">Awaiting display board data</div>
      )}
    </article>
  );
}
